import { useEffect, useRef } from 'react';
import { RouterProvider } from 'react-router-dom';
import { CircularProgress, ThemeProvider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { cliengoQueries } from '@cliengo/mfe-utils/hooks';
import { theme } from '@cliengo/mfe-utils/mod';
import CssReset from '@mui/material/CssBaseline';

import { Center } from './ui/Center';
import { identifyHubspot, setAnalytics } from './bootstrap';
import { initLaunchDarkly } from './clients/launchDarklyClient';

import { logout, authorizationGuard } from '@/utils/auth';
import { routerFactory } from '@/routes/routerFactory';
import { runOneSignal } from '@/services/onesignal.service';
import { sentryClient } from '@/clients/sentryClient';

export function Main() {
  const appIsReadyToGo = useRef(false);

  const userQuery = cliengoQueries.user();

  const accountQuery = cliengoQueries.account();

  const router = routerFactory(accountQuery.data);

  useQuery({
    queryKey: ['init-launchdarkly'],
    queryFn: () => initLaunchDarkly(userQuery.data, accountQuery.data),
    refetchOnWindowFocus: false,
    enabled: userQuery.isSuccess && accountQuery.isSuccess,
  });

  useEffect(() => {
    authorizationGuard();
  }, []);

  useEffect(() => {
    if (
      userQuery.status === 'success' &&
      accountQuery.status === 'success' &&
      !appIsReadyToGo.current
    ) {
      setAnalytics(accountQuery.data, userQuery.data);
      identifyHubspot(accountQuery.data, userQuery.data);
      runOneSignal(userQuery.data, accountQuery.data).catch(console.error);
      sentryClient.setCurrentUser(userQuery.data, accountQuery.data);
      appIsReadyToGo.current = true;
    }

    if (userQuery.status === 'error' || accountQuery.status === 'error') {
      console.error('Error featching user or account');
      console.error(`User: ${JSON.stringify(userQuery.error)}`);
      console.error(`Account: ${JSON.stringify(accountQuery.error)}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQuery.status, accountQuery.status]);

  // el token no es valido
  if (userQuery.error && userQuery.error.message.includes('403')) {
    logout();
  }

  if (!appIsReadyToGo.current) {
    return (
      <Center
        sx={{
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Center>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssReset />
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}
